import { ClipboardList, ArrowRight } from 'lucide-react'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { TechnologyCta } from '@/types/technology'

/* The closing call to action for every technology page.
 *
 * There is exactly ONE action and it opens the enquiry form. This section
 * publishes no prices and has no self-serve sign-up, so telling us what you
 * run is genuinely the only next step.
 *
 * The address is also rendered as visible text under the button, not hidden
 * inside the href. A mailto: is dead weight to anyone reading on a machine
 * with no mail client configured, and this page's audience skews exactly that
 * way. Seeing the address means they can copy it.
 */
export default function TechCta({ cta }: { cta: TechnologyCta }) {
  return (
    <section className="container mx-auto px-6 pb-24 pt-4">
      <ScrollReveal>
        <div className="relative mx-auto max-w-4xl overflow-hidden rounded-[1.75rem] border border-[#ff6a1a]/20 bg-gradient-to-br from-[#ff6a1a]/[0.08] to-white/[0.02] px-6 py-12 text-center sm:px-12">
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#ff6a1a]/[0.10] blur-3xl"
            aria-hidden="true"
          />
          <div className="relative">
            <h2
              className="marketing-section-title mx-auto text-white"
              style={{ fontSize: 'clamp(1.6rem, 3.2vw, 2.4rem)' }}
            >
              {cta.title}
            </h2>
            <p className="marketing-lead mx-auto mt-5 max-w-2xl">{cta.body}</p>

            <div className="mt-9 flex flex-col items-center gap-4">
              <a href={cta.action.href} className="marketing-button-primary">
                <ClipboardList className="h-4 w-4" />
                {cta.action.label}
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={`mailto:${cta.email}`}
                className="marketing-inline-link font-mono text-sm underline decoration-white/20 underline-offset-4 hover:!text-[#ff6a1a]"
              >
                {cta.email}
              </a>
            </div>

            <p className="mx-auto mt-8 max-w-xl text-[0.8125rem] leading-relaxed text-white/40">
              {cta.note}
            </p>
          </div>
        </div>
      </ScrollReveal>
    </section>
  )
}
